import { computed, ref, watch } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import type { SelectLine, SelectLineValueType } from '@/objects';
import { nameofEnum } from '@/utilities/utils';
import { type BibAssignSortType, type BibAssignPlaceUnseededType, BibAssign } from '@/objects/generated';
import { LoaderWithReset } from '@/utilities/LoaderWithReset';
import { BaseObjDao } from '@/dao';
import { useEventStoreRef } from './eventStore';

export type BibAssignValues = 'BibAssign' | 'BibAssignSortType' | 'BibAssignPlaceUnseeded';

export const useBibAssignStore = defineStore('bibassign', () => {
    // We need to listen to changes to the event
    const { eventID, initpromise } = useEventStoreRef();

    /**
     * A loader for the bibAssign
     */
    const bibAssignLoader = ref(
        new LoaderWithReset<BibAssign[]>({
            defaultValue: new Array<BibAssign>(),
            loaderFunction: async () => {
                const bibAssigns = await BaseObjDao.Load<BibAssign>(BibAssign, eventID.value);
                return bibAssigns;
            },
            waitOnBeforeLoad: initpromise.value,
        }),
    );

    /**
     * Watch for changes to the event ID and reset the loader whenever we change the event.
     */
    watch(
        eventID,
        async (nv, ov) => {
            if (nv == ov || nv == 0) {
                return;
            }

            // Reset all loaders
            await bibAssignLoader.value.ResetAndReload();
        },
        { immediate: true },
    );

    const BibAssignSortTypeAsSelectLine = ref<Map<SelectLineValueType, SelectLine>>(
        new Map([
            [
                nameofEnum<BibAssignSortType>('CompetitorSeedingGroup'),
                { text: 'bibassignsort.competitorseedinggroup', value: nameofEnum<BibAssignSortType>('CompetitorSeedingGroup') },
            ],
            [
                nameofEnum<BibAssignSortType>('CompetitorSeeding'),
                { text: 'bibassignsort.competitorseeding', value: nameofEnum<BibAssignSortType>('CompetitorSeeding') },
            ],
            [
                nameofEnum<BibAssignSortType>('SeedingList'),
                { text: 'bibassignsort.seedinglist', value: nameofEnum<BibAssignSortType>('SeedingList') },
            ],
            [nameofEnum<BibAssignSortType>('Series'), { text: 'bibassignsort.series', value: nameofEnum<BibAssignSortType>('Series') }],
            [nameofEnum<BibAssignSortType>('Result'), { text: 'bibassignsort.result', value: nameofEnum<BibAssignSortType>('Result') }],
            [
                nameofEnum<BibAssignSortType>('CategorySort'),
                { text: 'bibassignsort.categorysort', value: nameofEnum<BibAssignSortType>('CategorySort') },
            ],
            [
                nameofEnum<BibAssignSortType>('GroupSort'),
                { text: 'bibassignsort.groupsort', value: nameofEnum<BibAssignSortType>('GroupSort') },
            ],
            [
                nameofEnum<BibAssignSortType>('ClubTeam'),
                { text: 'bibassignsort.clubteam', value: nameofEnum<BibAssignSortType>('ClubTeam') },
            ],
            [nameofEnum<BibAssignSortType>('Team'), { text: 'bibassignsort.team', value: nameofEnum<BibAssignSortType>('Team') }],
            [nameofEnum<BibAssignSortType>('Club'), { text: 'bibassignsort.club', value: nameofEnum<BibAssignSortType>('Club') }],
            [
                nameofEnum<BibAssignSortType>('FreeTextClub'),
                { text: 'bibassignsort.freetextclub', value: nameofEnum<BibAssignSortType>('FreeTextClub') },
            ],
            [
                nameofEnum<BibAssignSortType>('FirstName'),
                { text: 'bibassignsort.firstname', value: nameofEnum<BibAssignSortType>('FirstName') },
            ],
            [
                nameofEnum<BibAssignSortType>('LastName'),
                { text: 'bibassignsort.lastname', value: nameofEnum<BibAssignSortType>('LastName') },
            ],
            [
                nameofEnum<BibAssignSortType>('Birthdate'),
                { text: 'bibassignsort.birthdate', value: nameofEnum<BibAssignSortType>('Birthdate') },
            ],
            [nameofEnum<BibAssignSortType>('Gender'), { text: 'bibassignsort.gender', value: nameofEnum<BibAssignSortType>('Gender') }],
            [
                nameofEnum<BibAssignSortType>('SignupTime'),
                { text: 'bibassignsort.signuptime', value: nameofEnum<BibAssignSortType>('SignupTime') },
            ],
            [
                nameofEnum<BibAssignSortType>('QueueNumber'),
                { text: 'bibassignsort.queuenumber', value: nameofEnum<BibAssignSortType>('QueueNumber') },
            ],
            [nameofEnum<BibAssignSortType>('Random'), { text: 'bibassignsort.random', value: nameofEnum<BibAssignSortType>('Random') }],
        ]),
    );

    const BibAssignPlaceUnseededAsSelectLine = ref<Map<SelectLineValueType, SelectLine>>(
        new Map([
            [
                nameofEnum<BibAssignPlaceUnseededType>('Last'),
                { text: 'bibassignsort.place_unseeded_last', value: nameofEnum<BibAssignPlaceUnseededType>('Last') },
            ],
            [
                nameofEnum<BibAssignPlaceUnseededType>('First'),
                { text: 'bibassignsort.place_unseeded_first', value: nameofEnum<BibAssignPlaceUnseededType>('First') },
            ],
        ]),
    );

    const ensureLoaded = async (dataTypeToEnsureIsLoaded: BibAssignValues[]) => {
        const promises = [];

        if (dataTypeToEnsureIsLoaded.includes('BibAssign')) {
            promises.push(bibAssignLoader.value.Load());
        }

        await Promise.all(promises);
    };

    return {
        BibAssignSortTypeAsSelectLine,
        BibAssignPlaceUnseededAsSelectLine,
        EnsureLoaded: ensureLoaded,
        BibAssigns: computed(() => bibAssignLoader.value.Value),
        ReloadBibAssigns: async () => bibAssignLoader.value.Reload(),
    };
});

/**
 * Return a reactive reference to the BibAssign store.
 * @returns
 */
export const useBibAssignStoreRef = () => storeToRefs(useBibAssignStore());
